import { useMutation, useQueryClient } from "react-query";

import { getQuickRepliesQueryKey } from "api/services/messaging/hooks/useGetQuickReplies";
import type { PostQuickReplyArgs, QuickReply } from "api/services/messaging/schemas/quick_replies";
import { useApiClient } from "api/useApiClient";

export const usePostQuickReply = () => {
  const queryClient = useQueryClient();
  const client = useApiClient();

  return useMutation(({ text, userID }: PostQuickReplyArgs) => client.postQuickReply({ text, userID }), {
    onMutate: async (newQuickReply: PostQuickReplyArgs) => {
      const queryKey = getQuickRepliesQueryKey(newQuickReply.userID);

      await queryClient.cancelQueries(queryKey);
      const previousQuickReplies = queryClient.getQueryData<QuickReply[]>(queryKey);

      if (previousQuickReplies) {
        queryClient.setQueryData<QuickReply[]>(queryKey, [
          ...previousQuickReplies,
          {
            id: Math.random(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            text: newQuickReply.text,
            user_id: newQuickReply.userID,
          },
        ]);
      }

      return { previousQuickReplies };
    },
    onError: (error, variables, context) => {
      const queryKey = getQuickRepliesQueryKey(variables.userID);

      if (context?.previousQuickReplies) {
        queryClient.setQueryData<QuickReply[]>(queryKey, context.previousQuickReplies);
      }
    },
    onSettled: context => {
      if (context) {
        queryClient.invalidateQueries(getQuickRepliesQueryKey(context.user_id));
      }
    },
  });
};
